import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { gbp } from "@/lib/pricing";

const COIN_NAMES = { BTC: "Bitcoin", LTC: "Litecoin" };
const LABEL = { awaiting_payment: "Awaiting Payment", confirming: "Confirming on-chain…", paid: "Payment Received", failed: "Payment Failed" };

export default function PayGroup() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const tq = token ? `?token=${encodeURIComponent(token)}` : "";
  const [group, setGroup] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => api.get(`/payment-groups/${id}${tq}`).then((r) => setGroup(r.data)).catch(() => setError("This payment could not be loaded. Check the link or contact support.")), [id, tq]);
  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(async (silent) => {
    if (!silent) setChecking(true);
    try {
      const { data } = await api.post(`/payment-groups/${id}/refresh-status${tq}`);
      setGroup(data);
      if (!silent) {
        if (data.payment_status === "paid") toast.success("Payment confirmed!");
        else if (data.payment_status === "confirming") toast.info("Payment detected — confirming on-chain");
        else toast.info("No payment detected yet");
      }
    } catch { if (!silent) toast.error("Payment status could not be checked."); } finally { if (!silent) setChecking(false); }
  }, [id, tq]);

  useEffect(() => {
    if (!group || ["paid", "failed"].includes(group.payment_status)) return;
    const t = setInterval(() => refresh(true), 10000);
    return () => clearInterval(t);
  }, [group, refresh]);

  if (error && !group) return <div className="mx-auto flex h-[50vh] max-w-xl items-center justify-center px-6 text-center text-sm text-destructive" data-testid="paygroup-error">{error}</div>;
  if (!group) return <div className="flex h-[50vh] items-center justify-center mono text-xs uppercase tracking-widest text-muted-foreground">Loading…</div>;

  const paid = group.payment_status === "paid";
  const confirming = group.payment_status === "confirming";
  const failed = group.payment_status === "failed";
  const coin = COIN_NAMES[group.currency] || group.currency;

  return (
    <div className="mx-auto max-w-3xl px-6 py-14" data-testid="paygroup-page">
      <div className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Combined payment {group.group_number}</div>
      <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter" data-testid="paygroup-status">{LABEL[group.payment_status] || "Payment"}</h1>

      <div className={`mt-6 flex items-center gap-3 border p-4 ${paid ? "border-green-600 bg-green-50 dark:bg-green-950/20" : failed ? "border-destructive" : "border-border"}`}>
        {paid ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : failed ? <AlertTriangle className="h-5 w-5 text-destructive" /> : confirming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Clock className="h-5 w-5 text-muted-foreground" />}
        <span className="text-sm font-medium">
          {paid ? `All ${group.orders.length} orders are paid — we're preparing your shipments.` :
           failed ? "This payment failed or expired. Go back to any of the orders below to pay again." :
           confirming ? "Payment detected on the network — still unconfirmed. We are monitoring it automatically." :
           `One ${coin} payment covers all ${group.orders.length} orders below.`}
        </span>
      </div>

      <div className="mt-8 border border-border" data-testid="paygroup-orders">
        <div className="mono border-b border-border bg-muted px-5 py-3 text-[10px] uppercase tracking-[0.2em]">Orders in this payment</div>
        <div className="divide-y divide-border">
          {group.orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-3 p-4 text-sm" data-testid={`paygroup-order-${o.order_number}`}>
              <div>
                <span className="mono font-semibold">{o.order_number}</span>
                {o.tracking_pin && <span className="ml-3 text-xs text-muted-foreground">PIN <span className="mono">{o.tracking_pin}</span></span>}
              </div>
              <span className="mono text-xs uppercase text-muted-foreground">{LABEL[o.payment_status] || o.payment_status}</span>
              <span className="mono">{gbp(o.total)}</span>
            </div>
          ))}
          <div className="flex justify-between p-4 font-bold"><span>Total to pay</span><span className="mono" data-testid="paygroup-total">{gbp(group.total)}</span></div>
        </div>
      </div>

      {!paid && !failed && group.checkout_url && (
        <div className="mt-8 border border-border">
          <div className="mono border-b border-border bg-muted px-5 py-3 text-[10px] uppercase tracking-[0.2em]">Secure payment · {group.currency}</div>
          <div className="space-y-5 p-6">
            <a href={group.checkout_url} target="_blank" rel="noopener noreferrer" data-testid="paygroup-checkout"
              className="flex w-full items-center justify-center bg-foreground py-4 text-sm font-medium text-background">
              Open secure {coin} payment →
            </a>
            {group.deposit_address && (
              <div className="border border-dashed border-border p-3 text-xs text-muted-foreground">
                Or send exactly <b className="mono">{group.crypto_amount} {group.currency}</b> to <span className="mono break-all">{group.deposit_address}</span>
              </div>
            )}
            {confirming ? (
              <div className="border border-green-600/40 bg-green-50 p-4 text-center text-sm text-green-800 dark:bg-green-950/20 dark:text-green-300">Transaction found. This page updates automatically.</div>
            ) : (
              <button disabled={checking} onClick={() => refresh(false)} data-testid="paygroup-refresh"
                className="flex w-full items-center justify-center gap-2 border border-border py-4 text-sm font-medium">
                <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} /> {checking ? "Checking…" : "Check payment status"}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-8"><Link to="/track" className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">Track an order →</Link></div>
    </div>
  );
}
